/**
 * Analyzer: Direction Asymmetry (Long vs Short)
 *
 * When one direction prints and the other bleeds, the trader was
 * fighting the prevailing flow. This is one of the cleanest learnable
 * signals — you can't fix "I shouldn't have shorted", but you CAN
 * internalize "when longs work this hard, my shorts are counter-trend".
 *
 * Requires >= 2 trades in EACH direction to avoid single-trade noise.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../types';
import { fmt$ } from '../utils';

export const key = 'directionAsymmetry';

export const analyze = (ctx) => {
  const { session, enriched } = ctx;
  if (!session || !Array.isArray(enriched) || enriched.length < 4) return null;

  const longs = enriched.filter(x => x.trade.direction === 'long');
  const shorts = enriched.filter(x => x.trade.direction === 'short');
  if (longs.length < 2 || shorts.length < 2) return null;

  const longPnl = longs.reduce((s, x) => s + x.netPnl, 0);
  const shortPnl = shorts.reduce((s, x) => s + x.netPnl, 0);

  // Longs won, shorts bled (shorts losing > 50% of long gains)
  if (longPnl > 0 && shortPnl < 0 && Math.abs(shortPnl) > Math.abs(longPnl) * 0.5) {
    return makeObservation({
      id: `${key}:${session.id}:long-trend`,
      analyzer: key,
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.WARNING,
      category: CATEGORY.EXECUTION,
      title: `Longs printed, shorts bled — you fought the flow`,
      detail: `Longs ${fmt$(longPnl)} across ${longs.length} vs shorts ${fmt$(shortPnl)} across ${shorts.length}. Counter-trend shorts were the leak.`,
      action: 'Add a "trend filter" check to shorts when longs are working this hard.',
      evidence: { longPnl, shortPnl, longCount: longs.length, shortCount: shorts.length },
      metric: { label: 'Counter-trend drag', value: shortPnl, baseline: longPnl, unit: '$' },
      confidence: 0.78,
      priority: 70
    });
  }

  // Shorts won, longs bled
  if (shortPnl > 0 && longPnl < 0 && Math.abs(longPnl) > Math.abs(shortPnl) * 0.5) {
    return makeObservation({
      id: `${key}:${session.id}:short-trend`,
      analyzer: key,
      scope: 'session',
      scopeId: session.id,
      severity: SEVERITY.WARNING,
      category: CATEGORY.EXECUTION,
      title: `Shorts worked, longs lost — you fought the flow`,
      detail: `Shorts ${fmt$(shortPnl)} across ${shorts.length} vs longs ${fmt$(longPnl)} across ${longs.length}. Counter-trend longs were the leak.`,
      action: 'Add a "trend filter" check to longs when shorts are working this hard.',
      evidence: { longPnl, shortPnl, longCount: longs.length, shortCount: shorts.length },
      metric: { label: 'Counter-trend drag', value: longPnl, baseline: shortPnl, unit: '$' },
      confidence: 0.78,
      priority: 70
    });
  }

  return null;
};
